import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, Lock, Unlock } from "lucide-react";
import api from "../lib/api";
import { fmtMoney, fmtDate } from "../lib/format";
import DataTable from "../components/DataTable";
import StatusPill from "../components/StatusPill";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function ManualJournalDialog({ open, onClose, accounts }) {
  const qc = useQueryClient();
  const [desc, setDesc] = useState("");
  const [event, setEvent] = useState("Manual Adjustment");
  const [lines, setLines] = useState([{ account_code: "", debit: "", credit: "" }, { account_code: "", debit: "", credit: "" }]);

  const reset = () => { setDesc(""); setEvent("Manual Adjustment"); setLines([{ account_code: "", debit: "", credit: "" }, { account_code: "", debit: "", credit: "" }]); };
  const setLine = (i, k, v) => setLines((s) => s.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines((s) => [...s, { account_code: "", debit: "", credit: "" }]);
  const rmLine = (i) => setLines((s) => s.filter((_, idx) => idx !== i));

  const totDr = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totCr = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = totDr === totCr && totDr > 0;

  const save = async () => {
    const payload = {
      description: desc || "Manual journal entry", event, maker: "Current User",
      lines: lines.filter((l) => l.account_code).map((l) => {
        const acc = accounts.find((a) => a.code === l.account_code);
        return { account_code: l.account_code, account_name: acc?.name || l.account_code, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 };
      }),
    };
    try {
      await api.post("/journal-entries/manual", payload);
      toast.success("Manual entry submitted for approval");
      qc.invalidateQueries();
      reset(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="manual-je-dialog">
        <DialogHeader><DialogTitle>New Manual Journal Entry</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs">Event</Label><Input data-testid="mje-event" value={event} onChange={(e) => setEvent(e.target.value)} /></div>
            <div><Label className="text-xs">Description</Label><Input data-testid="mje-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Reason for adjustment" /></div>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_120px_120px_32px] gap-2 text-xs font-semibold text-slate-400 px-1">
              <span>Account</span><span className="text-right">Debit</span><span className="text-right">Credit</span><span />
            </div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_120px_120px_32px] gap-2 items-center">
                <Select value={l.account_code} onValueChange={(v) => setLine(i, "account_code", v)}>
                  <SelectTrigger data-testid={`mje-acct-${i}`} className="h-9"><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>{accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid={`mje-debit-${i}`} type="number" className="h-9 text-right" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} />
                <Input data-testid={`mje-credit-${i}`} type="number" className="h-9 text-right" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} />
                <button onClick={() => rmLine(i)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button data-testid="mje-add-line" variant="outline" size="sm" onClick={addLine}><Plus className="h-4 w-4 mr-1" /> Add Line</Button>
          </div>
          <div className={`flex justify-end gap-6 text-sm font-mono ${balanced ? "text-emerald-600" : "text-rose-600"}`}>
            <span>Dr {fmtMoney(totDr)}</span><span>Cr {fmtMoney(totCr)}</span>
            <span>{balanced ? "Balanced ✓" : "Unbalanced"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="mje-save" disabled={!balanced} className="bg-orange-600 hover:bg-orange-700 text-white" onClick={save}>Submit for Approval</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Accounting() {
  const qc = useQueryClient();
  const { data: tb } = useQuery({ queryKey: ["tb"], queryFn: () => api.get("/accounting/trial-balance") });
  const { data: pnl } = useQuery({ queryKey: ["pnl-stmt"], queryFn: () => api.get("/accounting/pnl-statement") });
  const { data: bs } = useQuery({ queryKey: ["bs"], queryFn: () => api.get("/accounting/balance-sheet") });
  const { data: jes = [] } = useQuery({ queryKey: ["jes"], queryFn: () => api.get("/journal-entries") });
  const { data: coa = [] } = useQuery({ queryKey: ["coa"], queryFn: () => api.get("/accounts") });
  const { data: rules = [] } = useQuery({ queryKey: ["rules"], queryFn: () => api.get("/posting-rules") });
  const { data: periods = [] } = useQuery({ queryKey: ["periods"], queryFn: () => api.get("/periods") });
  const [mjeOpen, setMjeOpen] = useState(false);

  const approve = async (r) => {
    try {
      await api.post(`/journal-entries/${r.id}/approve`, { checker: "Current User" });
      toast.success("Entry approved & posted");
      qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const reject = async (r) => { await api.post(`/journal-entries/${r.id}/reject`, { checker: "Current User" }); toast.success("Entry rejected"); qc.invalidateQueries(); };
  const toggleLock = async (p) => {
    await api.post(`/periods/${p.id}/${p.locked ? "unlock" : "lock"}`);
    toast.success(`Period ${p.key} ${p.locked ? "unlocked" : "locked"}`);
    qc.invalidateQueries();
  };

  const jeCols = [
    { key: "entry_date", label: "Date", render: (r) => <span className="text-xs">{fmtDate(r.entry_date)}</span> },
    { key: "event", label: "Event", render: (r) => <span className="text-xs font-medium">{r.event}</span> },
    { key: "description", label: "Description" },
    { key: "entry_type", label: "Type", render: (r) => <span className="text-xs">{r.entry_type || "Auto"}</span> },
    { key: "debit", label: "Amount", align: "right", render: (r) => fmtMoney(r.lines?.reduce((s, l) => s + (l.debit || 0), 0)) },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
    { key: "_a", label: "Actions", render: (r) => (
      r.status === "Pending Approval"
        ? <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <button data-testid={`approve-je-${r.id}`} onClick={() => approve(r)} className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-950 text-emerald-600" title="Approve"><Check className="h-4 w-4" /></button>
            <button data-testid={`reject-je-${r.id}`} onClick={() => reject(r)} className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600" title="Reject"><X className="h-4 w-4" /></button>
          </div>
        : <span className="text-xs text-slate-400">—</span>
    ) },
  ];
  const jeExpand = (r) => (
    <div>
      <table className="w-full text-sm mb-2">
        <thead><tr className="text-xs text-slate-400"><th className="text-left py-1">Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
        <tbody>{r.lines?.map((l, i) => (
          <tr key={i}><td className="py-1">{l.account_code} — {l.account_name}</td>
            <td className="text-right font-mono">{l.debit ? fmtMoney(l.debit) : ""}</td>
            <td className="text-right font-mono">{l.credit ? fmtMoney(l.credit) : ""}</td></tr>))}</tbody>
      </table>
      {(r.maker || r.checker) && <p className="text-xs text-slate-400">Maker: {r.maker || "—"} · Checker: {r.checker || "—"}</p>}
    </div>
  );

  const tbCols = [
    { key: "account_code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.account_code}</span> },
    { key: "account_name", label: "Account" }, { key: "type", label: "Type" },
    { key: "debit", label: "Debit", align: "right", render: (r) => fmtMoney(r.debit) },
    { key: "credit", label: "Credit", align: "right", render: (r) => fmtMoney(r.credit) },
    { key: "balance", label: "Balance", align: "right", render: (r) => fmtMoney(r.balance) },
  ];
  const coaCols = [
    { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
    { key: "name", label: "Account Name" },
    { key: "type", label: "Type", render: (r) => <StatusPill status={r.type === "Asset" ? "Active" : r.type === "Revenue" ? "Confirmed" : r.type === "Expense" ? "Draft" : "Closed"} /> },
  ];
  const ruleCols = [
    { key: "event", label: "Business Event", render: (r) => <span className="font-medium">{r.event}</span> },
    { key: "debit", label: "Debit Account", render: (r) => <span className="text-emerald-600 text-xs">{r.debit_account_code} {r.debit_account_name}</span> },
    { key: "credit", label: "Credit Account", render: (r) => <span className="text-rose-600 text-xs">{r.credit_account_code} {r.credit_account_name}</span> },
  ];

  return (
    <div data-testid="accounting-page">
      <Tabs defaultValue="journal">
        <TabsList className="mb-3 flex-wrap h-auto">
          <TabsTrigger value="journal" data-testid="tab-journal">Journal Entries</TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-tb">Trial Balance</TabsTrigger>
          <TabsTrigger value="pnl" data-testid="tab-pnl">P&L Statement</TabsTrigger>
          <TabsTrigger value="bs" data-testid="tab-bs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="coa" data-testid="tab-coa">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Posting Rules</TabsTrigger>
          <TabsTrigger value="periods" data-testid="tab-periods">Period Close</TabsTrigger>
        </TabsList>

        <TabsContent value="journal">
          <DataTable testid="je-table" title="General Journal — auto-posted + manual (maker-checker)" columns={jeCols} rows={jes} csvName="journal_entries"
            expandable={jeExpand} onAdd={() => setMjeOpen(true)} addLabel="Manual Entry" />
        </TabsContent>
        <TabsContent value="tb">
          <DataTable testid="tb-table" title="Trial Balance" columns={tbCols} rows={tb?.rows || []} csvName="trial_balance" searchable={false}
            right={tb && <span className="text-xs font-mono">Dr {fmtMoney(tb.total_debit)} · Cr {fmtMoney(tb.total_credit)}</span>} />
        </TabsContent>
        <TabsContent value="pnl">
          {pnl && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 max-w-2xl">
              <h3 className="font-bold mb-4">Profit & Loss Statement</h3>
              <p className="text-xs font-bold uppercase text-slate-400 mb-2">Revenue</p>
              {pnl.revenue.map((r) => <div key={r.account_code} className="flex justify-between text-sm py-1"><span>{r.account_name}</span><span className="font-mono">{fmtMoney(-r.balance)}</span></div>)}
              <div className="flex justify-between text-sm py-1 font-semibold border-t mt-1"><span>Total Revenue</span><span className="font-mono">{fmtMoney(pnl.total_revenue)}</span></div>
              <p className="text-xs font-bold uppercase text-slate-400 mb-2 mt-4">Expenses</p>
              {pnl.expense.map((r) => <div key={r.account_code} className="flex justify-between text-sm py-1"><span>{r.account_name}</span><span className="font-mono">{fmtMoney(r.balance)}</span></div>)}
              <div className="flex justify-between text-sm py-1 font-semibold border-t mt-1"><span>Total Expenses</span><span className="font-mono">{fmtMoney(pnl.total_expense)}</span></div>
              <div className="flex justify-between text-base py-2 font-bold border-t-2 mt-2"><span>Net Profit</span><span className={`font-mono ${pnl.net_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtMoney(pnl.net_profit)}</span></div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="bs">
          {bs && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6">
                <h3 className="font-bold mb-3">Assets</h3>
                {bs.assets.map((r) => <div key={r.account_code} className="flex justify-between text-sm py-1"><span>{r.account_name}</span><span className="font-mono">{fmtMoney(r.balance)}</span></div>)}
                <div className="flex justify-between font-bold border-t mt-2 pt-2"><span>Total Assets</span><span className="font-mono">{fmtMoney(bs.total_assets)}</span></div>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6">
                <h3 className="font-bold mb-3">Liabilities & Equity</h3>
                {bs.liabilities.map((r) => <div key={r.account_code} className="flex justify-between text-sm py-1"><span>{r.account_name}</span><span className="font-mono">{fmtMoney(-r.balance)}</span></div>)}
                {bs.equity.map((r) => <div key={r.account_code} className="flex justify-between text-sm py-1"><span>{r.account_name}</span><span className="font-mono">{fmtMoney(-r.balance)}</span></div>)}
                <div className="flex justify-between font-bold border-t mt-2 pt-2"><span>Total Liab. + Equity</span><span className="font-mono">{fmtMoney(bs.total_liabilities + bs.total_equity)}</span></div>
              </div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="coa">
          <DataTable testid="coa-table" title="Chart of Accounts" columns={coaCols} rows={coa} csvName="chart_of_accounts" />
        </TabsContent>
        <TabsContent value="rules">
          <DataTable testid="rules-table" title="Auto-Posting Rules — Event → GL Template" columns={ruleCols} rows={rules} csvName="posting_rules" searchable={false} />
        </TabsContent>
        <TabsContent value="periods">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 font-bold">Period Close — lock periods to block further posting</div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {[...periods].sort((a, b) => a.key.localeCompare(b.key)).map((p) => (
                <div key={p.id} data-testid={`period-${p.key}`} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-semibold">{p.key}</span>
                    <StatusPill status={p.locked ? "Closed" : "Active"} />
                  </div>
                  <Button data-testid={`period-toggle-${p.key}`} variant="outline" size="sm" onClick={() => toggleLock(p)}>
                    {p.locked ? <><Unlock className="h-4 w-4 mr-1" /> Unlock</> : <><Lock className="h-4 w-4 mr-1" /> Lock</>}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ManualJournalDialog open={mjeOpen} onClose={() => setMjeOpen(false)} accounts={coa} />
    </div>
  );
}
